import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useDoctorNotifications from "../../hooks/useDoctorNotifications";
import { ROUTES } from "../../routes";
import { getNotificationPreviewLine } from "../../utils/notificationMessage";

import "./doctor-notifications.css";

function playS4AlertSound() {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const playBeep = (startTime, frequency, duration) => {
            const osc = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(frequency, startTime);
            gainNode.gain.setValueAtTime(0.3, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
            osc.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            osc.start(startTime);
            osc.stop(startTime + duration);
        };
        const now = audioCtx.currentTime;
        playBeep(now, 587.33, 0.25);
        playBeep(now + 0.3, 587.33, 0.4);
    } catch (e) {
        console.warn("Failed to play S4 alert sound:", e);
    }
}

/**
 * Toast при появлении нового S4 из polling (prod-checklist).
 * Отдельно от ErrorHandler — глобальный shell без page-level provider.
 */
export default function NotificationS4Notifier() {
    const navigate = useNavigate();
    const { enabled, notifications, newNotificationIds } = useDoctorNotifications();
    const [activeToast, setActiveToast] = useState(null);
    const toastedIdsRef = useRef(new Set());

    // Обнаружение нового S4 (один раз на id за сессию)
    useEffect(() => {
        if (!enabled) {
            setActiveToast(null);
            toastedIdsRef.current.clear();
            return;
        }

        const freshS4 = notifications.find(
            (alert) =>
                alert.situation_class === "S4" &&
                newNotificationIds.includes(alert.id) &&
                !toastedIdsRef.current.has(alert.id)
        );

        if (!freshS4) return;

        toastedIdsRef.current.add(freshS4.id);
        setActiveToast(freshS4);
        playS4AlertSound();
    }, [enabled, notifications, newNotificationIds]);

    if (!activeToast) return null;

    const preview = getNotificationPreviewLine(activeToast);

    return (
        <div
            className="doctor-notification-s4-toast"
            role="alert"
            aria-live="assertive"
        >
            <div className="doctor-notification-s4-toast__content">
                <span className="doctor-notification-badge doctor-notification-badge--danger">S4</span>
                <div className="doctor-notification-s4-toast__text">
                    <strong>Новое критическое уведомление</strong>
                    <span>{activeToast.title}</span>
                    {preview ? <p>{preview}</p> : null}
                </div>
            </div>
            <div className="doctor-notification-s4-toast__actions">
                <button
                    type="button"
                    className="doctor-notification-btn doctor-notification-btn--primary"
                    onClick={() => {
                        setActiveToast(null);
                        navigate(ROUTES.NOTIFICATIONS, { viewTransition: true });
                    }}
                >
                    Открыть
                </button>
                <button
                    type="button"
                    className="doctor-notification-btn doctor-notification-btn--ghost"
                    onClick={() => setActiveToast(null)}
                    aria-label="Закрыть уведомление"
                >
                    ✕
                </button>
            </div>
        </div>
    );
}
